import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { isNativePlatform } from '@/lib/platform';
import { purchasesEnabled } from '@/lib/billing/purchaseGate';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { buttonVariants } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { toast as sonnerToast } from 'sonner';
import { postLoginRoute } from '@/lib/postLoginRoute';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { usePageMeta } from '@/hooks/usePageMeta';
import { setRememberMePreference } from '@/lib/auth/sessionPersistence';
import { getStoredRef, clearStoredRef } from '@/lib/referral';
import { Progress } from '@/components/ui/progress';
import { authRedirectUrl } from '@/lib/authRedirect';
import { openExternal } from '@/lib/openExternal';

import { getClockDriftMinutes, clearServerTimeCache } from '@/lib/serverTime';
import { Eye, EyeOff, AlertCircle, Mail, Phone, Truck, Briefcase, Package, CheckCircle, CheckCircle2, XCircle, CreditCard, Users, Minus, Plus, ChevronLeft, ChevronRight, Crown, Sparkles } from 'lucide-react';
import lanerunnerLogoCyan from '@/assets/lanerunner-logo-cyan.png';
import { TurnstileWidget, type TurnstileWidgetHandle } from '@/components/security/TurnstileWidget';
import SignupFmcsaStep from '@/components/auth/SignupFmcsaStep';

// 'onboarding-fmcsa' kept in the union for legacy sessionStorage rows that
// still reference it. No active transition targets this step — the FMCSA
// gate now lives post-login (see src/components/onboarding/VerifyMcGate.tsx).
type AuthStep = 'pick-role' | 'signup' | 'login' | 'email-verification' | 'phone-verification' | 'onboarding-role' | 'onboarding-fmcsa' | 'onboarding-shipper-profile' | 'onboarding-tier' | 'onboarding-plan';

const ONBOARDING_STEPS = ['Role', 'Account', 'Email', 'Phone', 'FMCSA', 'Tier'];

const ONBOARDING_STEPS_FULL = [
  'Select Account Type',
  'Create Account',
  'Verify Email',
  'Verify Phone',
  'Verify FMCSA',
  'Choose Tier',
];

const SIGNUP_ROLE_KEY = 'lr_signup_role_v1';

const BrandPanel = ({ progressStep, showTracker = true }: { progressStep: number; showTracker?: boolean }) => (
  <div className="hidden md:flex md:w-[20%] md:max-w-[20%] bg-[#0c1220] flex-col justify-between p-10 text-white">
    <div>
      <div className="flex items-center gap-2 mb-12">
        <img src={lanerunnerLogoCyan} alt="LaneRunner" className="h-8" />
      </div>
      {!showTracker ? (
        <>
          <h2 className="text-2xl font-bold mb-3">Welcome back</h2>
          <p className="text-white/60 text-sm leading-relaxed">
            Sign in to access your dashboard, manage loads, and keep your freight moving.
          </p>
        </>
      ) : (
        <>
      <h2 className="text-2xl font-bold mb-2">Get Started</h2>
      <p className="text-white/50 text-sm mb-8">Complete these steps to activate your account</p>
      <ol className="space-y-4">
        {ONBOARDING_STEPS_FULL.map((label, index) => {
          const isCompleted = index < progressStep;
          const isActive = index === progressStep;
          return (
            <li key={label} className="flex items-center gap-3">
              {isCompleted ? (
                <div className="w-7 h-7 rounded-full bg-[hsl(var(--brand-teal))] flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
              ) : isActive ? (
                <div className="w-7 h-7 rounded-full bg-[hsl(var(--brand-teal))]/15 ring-2 ring-[hsl(var(--brand-teal))] flex items-center justify-center flex-shrink-0">
                  <span className="text-[hsl(var(--brand-teal))] text-xs font-bold">{index + 1}</span>
                </div>
              ) : (
                <div className="w-7 h-7 rounded-full bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-white/40 text-xs font-medium">{index + 1}</span>
                </div>
              )}
              <span
                className={`text-sm transition-colors ${
                  isCompleted ? 'text-white/80' : isActive ? 'text-white font-semibold' : 'text-white/40'
                }`}
              >
                {label}
              </span>
            </li>
          );
        })}
      </ol>
        </>
      )}
    </div>
    <div className="mt-auto pt-6">
      {showTracker && (
        <>
          <div className="flex items-center gap-1 mb-2">
            {ONBOARDING_STEPS.map((step, index) => (
              <div key={step} className={`h-1 flex-1 rounded-full transition-all ${index <= progressStep ? 'bg-[hsl(var(--brand-teal))]' : 'bg-white/15'}`} />
            ))}
          </div>
          <span className="text-white/40 text-xs">{progressStep + 1}/{ONBOARDING_STEPS.length}</span>
        </>
      )}
      <p className="text-white/30 text-xs mt-6">© 2026 LaneRunner, Inc.</p>
    </div>
  </div>
);

interface FmcsaData {
  legalName: string;
  dbaName?: string;
  dotNumber?: string;
  mcNumber?: string;
  statusCode?: string;
  phyStreet?: string | null;
  phyCity?: string | null;
  phyState?: string | null;
  phyZipcode?: string | null;
  entityType?: string | null;
}

export default function AuthPage() {
  usePageMeta({
    title: 'Login & Signup',
    description: 'Sign in to your LANERUNNER account or create a new account. Access your broker or carrier dashboard to manage loads and freight operations.'
  });
  
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { user, loading, refreshProfile } = useAuth();
  
  // State management
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>(() => {
    if (typeof window === 'undefined') return 'login';
    const m = new URLSearchParams(window.location.search).get('mode');
    const isSignupPath = window.location.pathname === '/signup';
    return (m === 'signup' || isSignupPath) ? 'signup' : 'login';
  });
  const [authStep, setAuthStep] = useState<AuthStep>(() => {
    if (typeof window === 'undefined') return 'pick-role';
    const m = new URLSearchParams(window.location.search).get('mode');
    const isSignupPath = window.location.pathname === '/signup';
    // mode=login → first render is the login form, no role-picker flash.
    // mode=signup, /signup path, (or anything else) → keep existing 'pick-role'
    // first render so signup flow, anti-bounce guards, isSignupInProgress,
    // handleSignup, and Effect B all behave identically.
    if (isSignupPath) return 'pick-role';
    return m === 'login' ? 'login' : 'pick-role';
  });
  const manualNavRef = useRef(0);
  const [userId, setUserId] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userPhone, setUserPhone] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<'carrier' | 'broker' | 'shipper' | 'broker_carrier' | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [generatedOtp, setGeneratedOtp] = useState<string | null>(null);
  const [canResendOtp, setCanResendOtp] = useState(true);
  
  // Email OTP state
  const [emailOtpCode, setEmailOtpCode] = useState('');
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [canResendEmailOtp, setCanResendEmailOtp] = useState(true);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const emailOtpAutoSentRef = useRef(false);
  const phoneOtpAutoSentRef = useRef(false);
  const tierAutoRouteRef = useRef(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);
  
  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  
  // Signup form state
  const [signupFullName, setSignupFullName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPhone, setSignupPhone] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirmPassword, setSignupConfirmPassword] = useState('');
  const [signupAcceptedTerms, setSignupAcceptedTerms] = useState(false);
  const [signupSmsConsent, setSignupSmsConsent] = useState(false);

  // Shipper-only profile (collected between phone OTP and tier selection)
  const [shipperIndustry, setShipperIndustry] = useState<string>('');
  const [shipperMonthlyVolume, setShipperMonthlyVolume] = useState<string>('');
  const [isSavingShipperProfile, setIsSavingShipperProfile] = useState(false);


  // Phone formatting & validation
  const formatPhoneDisplay = (digits: string) => {
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 10);
    setSignupPhone(raw);
  };

  const VALID_AREA_CODE_REGEX = /^[2-9][0-8]\d$/;
  const isPhoneValid = (() => {
    if (signupPhone.length !== 10) return false;
    if (/^(\d)\1{9}$/.test(signupPhone)) return false;
    if (signupPhone === '1234567890' || signupPhone === '0987654321') return false;
    if (!VALID_AREA_CODE_REGEX.test(signupPhone.slice(0, 3))) return false;
    return true;
  })();
  const [passwordFocused, setPasswordFocused] = useState(false);

  const hasMinLength = signupPassword.length >= 8;
  const hasLetter = /[a-zA-Z]/.test(signupPassword);
  const hasNumber = /\d/.test(signupPassword);
  const isPasswordValid = hasMinLength && hasLetter && hasNumber;
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showSignupConfirmPassword, setShowSignupConfirmPassword] = useState(false);
  
  // Password reset state
  const [resetEmail, setResetEmail] = useState('');
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  // FMCSA capture state removed from signup funnel — verification now happens
  // post-login in VerifyMcGate. `verifiedFmcsaData` is retained because Effect B
  // hydrates it from `profiles` for already-verified returning users so the
  // checkout step can still pass company metadata to Stripe.
  const [verifiedFmcsaData, setVerifiedFmcsaData] = useState<FmcsaData | null>(null);
  
  // Plan selection state
  const [seats, setSeats] = useState(1);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
  const [selectedTier, setSelectedTier] = useState<'free' | 'premium' | 'lr_direct' | null>(null);
  const [isProvisioningFree, setIsProvisioningFree] = useState(false);
  
  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [clockDriftMinutes, setClockDriftMinutes] = useState<number | null>(null);
  // (isVerifyingFmcsa removed with the FMCSA signup step)

  // Start over (delete incomplete signup) state
  const [startOverOpen, setStartOverOpen] = useState(false);
  const [startOverLoading, setStartOverLoading] = useState(false);

  const handleStartOver = () => {
    console.log('[start-over] clicked, user:', user?.id ?? null, 'authStep:', authStep);
    if (!user) {
      // Pre-submit: nothing to delete. Hard-reset to pick-role via full reload
      // so no Effect can override the step.
      try { sessionStorage.removeItem(SIGNUP_ROLE_KEY); } catch {}
      try { resetSignupUiState(); } catch {}
      window.location.assign('/auth?mode=signup');
      return;
    }
    setStartOverOpen(true);
  };

  const confirmStartOver = async () => {
    setStartOverLoading(true);
    try {
      const { error } = await supabase.functions.invoke('delete-incomplete-signup');
      if (error) {
        console.error('[start-over] edge function error:', error);
        toast({
          title: 'Could not start over',
          description: error.message ?? 'Please try again.',
          variant: 'destructive',
        });
        setStartOverLoading(false);
        return;
      }
      resetSignupUiState();
      resetLoginUiState();
      try { sessionStorage.removeItem(SIGNUP_ROLE_KEY); } catch {}
      try { await supabase.auth.signOut({ scope: 'local' }); } catch {}
      manualNavRef.current = Date.now();
      setStartOverOpen(false);
      setStartOverLoading(false);
      toast({
        title: 'Signup cancelled',
        description: 'You can start fresh.',
      });
      window.location.assign('/auth?mode=signup');
    } catch (e: any) {
      console.error('[start-over] unexpected error:', e);
      toast({
        title: 'Could not start over',
        description: e?.message ?? 'Unexpected error.',
        variant: 'destructive',
      });
      setStartOverLoading(false);
    }
  };

  const startOverDialog = (
    <AlertDialog open={startOverOpen} onOpenChange={(open) => { if (!startOverLoading) setStartOverOpen(open); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Start over?</AlertDialogTitle>
          <AlertDialogDescription>
            This will delete your progress and return you to role selection.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={startOverLoading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); confirmStartOver(); }}
            disabled={startOverLoading}
            className={buttonVariants({ variant: 'destructive' })}
          >
            {startOverLoading ? 'Deleting…' : 'Yes, start over'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
  
  const [searchParams] = useSearchParams();

  // Reset helpers used when transitioning between login and signup via nav links
  const resetSignupUiState = () => {
    setSelectedRole(null);
    setUserId(null); setUserEmail(null); setUserPhone(null);
    setOtpCode(''); setGeneratedOtp(null); setCanResendOtp(true);
    setEmailOtpCode(''); setEmailOtpSent(false); setIsEmailVerified(false);
    setIsPhoneVerified(false);
    setCanResendEmailOtp(true); emailOtpAutoSentRef.current = false;
    phoneOtpAutoSentRef.current = false;
    setVerifiedFmcsaData(null);
    setSignupFullName(''); setSignupEmail(''); setSignupPhone('');
    setSignupPassword(''); setSignupConfirmPassword('');
    setSignupAcceptedTerms(false); setSignupSmsConsent(false);
  };
  const resetLoginUiState = () => {
    setLoginEmail(''); setLoginPassword(''); setShowLoginPassword(false);
  };

  // In-form tab switch helper (Login ↔ Sign Up tabs and inline links inside the auth card).
  // Bumps manualNavRef FIRST so Effect B's 2-second guard skips and won't clobber the new step.
  const handleTabChange = (tab: 'login' | 'signup') => {
    manualNavRef.current = Date.now();
    if (tab === 'login') {
      resetSignupUiState();
      setActiveTab('login');
      setAuthStep('login');
    } else {
      resetLoginUiState();
      setActiveTab('signup');
      setAuthStep(selectedRole ? 'signup' : 'pick-role');
    }
  };

  // Honor ?mode=login|signup deep-link from public marketing CTAs
  // Cached role is only restored when ?resume=1 is present (explicit intent).
  useEffect(() => {
    manualNavRef.current = Date.now(); // FIRST — protect against Effect B clobber
    const mode = searchParams.get('mode');
    const resume = searchParams.get('resume') === '1';

    // Phase D — persist a network-invite token from the email magic link so it
    // survives signup + Stripe redirect. Auto-accept fires later from
    // LRDirectGateProvider once the broker's subscription is active.
    const inviteParam = searchParams.get('invite');
    if (inviteParam) {
      try { sessionStorage.setItem('lr_pending_network_invite_token', inviteParam); } catch {}
    }

    if (mode === 'login') {
      resetSignupUiState();
      setActiveTab('login');
      setAuthStep('login');
    } else if (mode === 'signup') {
      // Don't clobber an in-flight signup back to pick-role on URL re-eval
      if (isSignupInProgress()) return;
      resetLoginUiState();
      setActiveTab('signup');

      // ?role= from pricing/landing CTAs (additive — UI only).
      // Maps lr_direct_* aliases to the existing broker user_type and stashes
      // the tier hint for the pricing-page highlight on a back nav.
      const roleParam = (searchParams.get('role') || '').toLowerCase();
      const tierParam = (searchParams.get('tier') || '').toLowerCase();
      const roleMap: Record<string, typeof selectedRole> = {
        broker: 'broker',
        carrier: 'carrier',
        shipper: 'shipper',
        broker_carrier: 'broker_carrier',
        board_only: 'broker',
        lr_direct_private: 'broker',
        lr_direct: 'broker',
      };
      const mappedRole = roleParam ? roleMap[roleParam] ?? null : null;
      const tierHint = tierParam || (roleParam === 'lr_direct_private' ? 'lr_direct_private'
        : roleParam === 'board_only' ? 'board_only'
        : roleParam === 'lr_direct' ? 'lr_direct' : '');
      if (tierHint) {
        try { sessionStorage.setItem('lr_signup_tier_v1', tierHint); } catch {}
      }

      const cachedRole = resume ? (() => {
        try { return sessionStorage.getItem(SIGNUP_ROLE_KEY) as typeof selectedRole; } catch { return null; }
      })() : null;
      if (mappedRole) {
        setSelectedRole(mappedRole);
        try { sessionStorage.setItem(SIGNUP_ROLE_KEY, mappedRole as string); } catch {}
        setAuthStep('signup');
      } else if (cachedRole) {
        setSelectedRole(cachedRole);
        setAuthStep('signup');
      } else {
        // Fresh signup entry — drop stale role so we don't inherit a prior abandoned attempt.
        if (!resume) {
          resetSignupUiState();
          try { sessionStorage.removeItem(SIGNUP_ROLE_KEY); } catch {}
        }
        setAuthStep('pick-role');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  // Phase D — if a logged-in broker hits /auth with an invite token (e.g. they
  // clicked the email link in another tab while still authenticated), skip the
  // signup flow and send them straight to the in-app acceptance page.
  useEffect(() => {
    if (loading || !user) return;
    const inviteParam = searchParams.get('invite');
    let stashed: string | null = null;
    try { stashed = sessionStorage.getItem('lr_pending_network_invite_token'); } catch {}
    const token = inviteParam || stashed;
    if (!token) return;
    // Don't preempt a signup-in-progress (broker is mid-flow; the auto-accept
    // hook in LRDirectGateProvider will handle them post-checkout).
    if (isSignupInProgress()) return;
    navigate(`/lr-direct/network-invitations?token=${encodeURIComponent(token)}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, searchParams.toString()]);

  // Persist signup progress to sessionStorage so a refresh doesn't lose it
  const SIGNUP_CACHE_KEY = 'lr_signup_progress_v1';
  const SIGNUP_CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

  const saveSignupProgress = (data: { userId: string; email: string; phone: string; step: AuthStep }) => {
    try {
      sessionStorage.setItem(SIGNUP_CACHE_KEY, JSON.stringify({ ...data, ts: Date.now() }));
    } catch {}
  };

  const clearSignupProgress = () => {
    try { sessionStorage.removeItem(SIGNUP_CACHE_KEY); } catch {}
  };

  const loadSignupProgress = (): { userId: string; email: string; phone: string; step: AuthStep } | null => {
    try {
      const raw = sessionStorage.getItem(SIGNUP_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (Date.now() - parsed.ts > SIGNUP_CACHE_TTL_MS) {
        sessionStorage.removeItem(SIGNUP_CACHE_KEY);
        return null;
      }
      return parsed;
    } catch { return null; }
  };

  // Shared guard: returns true when a signup is mid-flight and reset paths must no-op.
  // Prevents ?mode=signup re-evaluations from bouncing the user back to pick-role
  // after they've chosen a role, submitted the form, or cached signup progress.
  const isSignupInProgress = (): boolean => {
    const inProgressSteps: AuthStep[] = ['signup', 'phone-verification', 'email-verification', 'onboarding-role', 'onboarding-fmcsa', 'onboarding-shipper-profile', 'onboarding-tier', 'onboarding-plan'];
    if (selectedRole) return true;
    if (userId) return true;
    if (inProgressSteps.includes(authStep)) return true;
    try {
      if (sessionStorage.getItem(SIGNUP_CACHE_KEY)) return true;
    } catch {}
    return false;
  };

  // Check clock drift on mount
  useEffect(() => {
    getClockDriftMinutes(true).then((drift) => {
      if (drift > 2) setClockDriftMinutes(drift);
      else setClockDriftMinutes(null);
    }).catch(() => {});
  }, []);


  // Show forced sign-out message after redirect (toast would otherwise disappear on hard reload)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('lr_auth_flash_v1');
      if (!raw) return;
      sessionStorage.removeItem('lr_auth_flash_v1');

      const msg = JSON.parse(raw) as {
        title?: string;
        description?: string;
        variant?: 'default' | 'destructive';
        duration?: number;
      };

      toast({
        title: msg.title ?? 'Signed Out',
        description: msg.description ?? 'You were signed out.',
        variant: msg.variant ?? 'destructive',
        duration: msg.duration ?? 30000,
      });
    } catch {
      // ignore
    }
  }, [toast]);

  // Keep signup cache in sync as the user progresses through steps
  useEffect(() => {
    const onboardingSteps: AuthStep[] = ['email-verification', 'phone-verification', 'onboarding-role', 'onboarding-fmcsa', 'onboarding-shipper-profile', 'onboarding-tier', 'onboarding-plan'];
    if (userId && userEmail && onboardingSteps.includes(authStep)) {
      saveSignupProgress({ userId, email: userEmail, phone: userPhone || '', step: authStep });
    }
  }, [authStep, userId, userEmail, userPhone]);

  // Check user status on mount and determine which step to show
  useEffect(() => {
    const effectBStart = performance.now();
    console.log(`[EFFECT-B] triggered: user=${!!user}, loading=${loading}, user.id=${user?.id?.slice(-8) ?? 'null'}`);
    const checkUserStatus = async () => {
      if (loading && !user) return;
      // Skip auto-advance if user manually navigated back within last 2 seconds
      if (Date.now() - manualNavRef.current < 2000) {
        return;
      }
      // If we're already mid-onboarding (handleSignup just set this), don't
      // re-evaluate — the SIGNED_IN event must not bounce us back to pick-role.
      const onboardingSteps: AuthStep[] = ['email-verification', 'phone-verification', 'onboarding-role', 'onboarding-fmcsa', 'onboarding-shipper-profile', 'onboarding-tier', 'onboarding-plan'];
      if (user && onboardingSteps.includes(authStep)) {
        console.log(`[EFFECT-B] skip: already on onboarding step ${authStep}`);
        return;
      }
      const mode = searchParams.get('mode');
      const resume = searchParams.get('resume') === '1';

      if (!user) {
        // ?mode=login → always show login form, never resume cached signup progress
        if (mode === 'login') {
          clearSignupProgress();
          try { sessionStorage.removeItem(SIGNUP_ROLE_KEY); } catch {}
          setSelectedRole(null);
          setAuthStep('login');
          setActiveTab('login');
          return;
        }

        // Restore signup cache ONLY when explicitly resuming. Otherwise start clean.
        const cached = resume ? loadSignupProgress() : null;
        if (mode === 'signup' && (!cached || cached.step === 'onboarding-plan' || cached.step === 'onboarding-tier' || cached.step === 'onboarding-fmcsa')) {
          // Don't clobber an in-flight signup back to pick-role
          if (isSignupInProgress()) return;
          // Fresh signup OR stale late-stage cache → reset to role picker
          clearSignupProgress();
          try { sessionStorage.removeItem(SIGNUP_ROLE_KEY); } catch {}
          setSelectedRole(null);
          setAuthStep('pick-role');
          return;
        }
        if (cached) {
          setUserId(cached.userId);
          setUserEmail(cached.email);
          setUserPhone(cached.phone);
          setAuthStep(cached.step);
        } else {
          setAuthStep(activeTab);
        }
        return;
      }

      // User is logged in, check their profile status
      try {
        console.log(`[EFFECT-B] user present, fetching profile @ ${(performance.now() - effectBStart).toFixed(0)}ms`);
        const { data: profile } = await supabase
          .from('profiles')
          .select('email_verified, phone_verified, onboarding_completed, phone, email, user_type, fmcsa_verified, fmcsa_legal_name, fmcsa_dba_name, fmcsa_dot_number, fmcsa_mc_number, fmcsa_status_code, fmcsa_manual_pending')
          .eq('id', user.id)
          .maybeSingle();
        console.log(`[EFFECT-B] profile fetched @ ${(performance.now() - effectBStart).toFixed(0)}ms, onboarding_completed=${profile?.onboarding_completed}`);

        if (!profile) {
          console.error('Profile not found for user');
          return;
        }

        // ?mode=login intent: show login form. If the signed-in user is a fully
        // onboarded account, send them to their dashboard. If they're an incomplete
        // onboarding session, locally sign them out so login renders cleanly — and
        // do NOT bounce them back into onboarding from this entry point.
        if (mode === 'login') {
          if (profile.onboarding_completed) {
            clearSignupProgress();
            const dashboardPath = postLoginRoute(profile.user_type);
            navigate(dashboardPath);
            return;
          }
          // Incomplete profile → drop the stale session locally, show login form.
          try { await supabase.auth.signOut({ scope: 'local' }); } catch {}
          clearSignupProgress();
          try { sessionStorage.removeItem(SIGNUP_ROLE_KEY); } catch {}
          setSelectedRole(null);
          setAuthStep('login');
          setActiveTab('login');
          return;
        }

        // Cache restore guard: only honor cache if it belongs to THIS signed-in user
        // and the caller explicitly asked to resume.
        const cached = resume ? loadSignupProgress() : null;
        const cacheBelongsToUser = cached && cached.userId === user.id;
        if (cached && !cacheBelongsToUser) {
          clearSignupProgress();
        }

        // Set basic user info
        setUserId(user.id);
        setUserEmail(profile.email || user.email || null);
        setUserPhone(profile.phone || null);
        setIsEmailVerified(!!profile.email_verified);
        setIsPhoneVerified(!!profile.phone_verified);

        if (profile.onboarding_completed) {
          // All done, redirect to dashboard
          clearSignupProgress();
          const dashboardPath = postLoginRoute(profile.user_type);
          console.log(`[EFFECT-B] navigate(${dashboardPath}) @ ${(performance.now() - effectBStart).toFixed(0)}ms`);
          navigate(dashboardPath);
        } else if (!profile.phone_verified) {
          // Only force phone-verification for users actively in the signup flow.
          // For everyone else (password-reset returnees, internal-provisioned
          // accounts, legacy accounts with phone_verified=false), drop the
          // stale session locally and show the login form — same recovery
          // the mode=login branch above uses. Prevents an OTP dead-end where
          // the UI shows a code input but no phone-entry path.
          const inSignup = isSignupInProgress() && !!profile.phone;
          if (inSignup) {
            console.log('[Effect B] forcing phone-verification (active signup), profile.email_verified:', profile.email_verified, 'profile.phone_verified:', profile.phone_verified);
            setAuthStep('phone-verification');
          } else {
            console.log('[Effect B] phone_verified=false but not in signup — dropping session, showing login');
            try { await supabase.auth.signOut({ scope: 'local' }); } catch {}
            clearSignupProgress();
            try { sessionStorage.removeItem(SIGNUP_ROLE_KEY); } catch {}
            setSelectedRole(null);
            setAuthStep('login');
            setActiveTab('login');
          }
        } else if (!profile.email_verified) {
          setAuthStep('email-verification');
        } else if (!profile.user_type) {
          setAuthStep('onboarding-role');
        } else {
          // Returning mid-signup: brokers/carriers must have FMCSA on file
          // before continuing to tier selection. Shippers skip FMCSA.
          const uType = profile.user_type as 'carrier' | 'broker' | 'shipper' | 'broker_carrier';
          setSelectedRole(uType);
          if (profile.fmcsa_verified) {
            setVerifiedFmcsaData({
              legalName: profile.fmcsa_legal_name || '',
              dbaName: profile.fmcsa_dba_name || undefined,
              dotNumber: profile.fmcsa_dot_number || undefined,
              mcNumber: profile.fmcsa_mc_number || undefined,
              statusCode: profile.fmcsa_status_code || undefined,
            });
            setAuthStep('onboarding-tier');
          } else if (uType === 'shipper') {
            setAuthStep('onboarding-tier');
          } else {
            // Broker / carrier / broker_carrier without FMCSA → back to FMCSA step.
            // Also treat a filed manual submission (fmcsa_manual_pending) as complete.
            const manualPending = !!(profile as any).fmcsa_manual_pending;
            setAuthStep(manualPending ? 'onboarding-tier' : 'onboarding-fmcsa');
          }
        }
      } catch (error) {
        console.error('Error checking user status:', error);
      }
    };

    checkUserStatus();
  }, [user, loading, navigate, activeTab, searchParams]);

  // Handle login
  const handleLogin = async () => {
    const t0 = performance.now();
    console.log(`[LOGIN-TIMING] click start`);
    setIsLoading(true);

    // Persist remember-me preference BEFORE login attempt
    setRememberMePreference(rememberMe);

    try {
      console.log(`[LOGIN-TIMING] calling signInWithPassword @ ${(performance.now() - t0).toFixed(0)}ms`);
      const { data, error } = await supabase.auth.signInWithPassword({
        // Normalize: iOS keyboards/autofill frequently append a trailing space
        // and capitalize the first letter. Supabase does NOT trim, so an
        // untrimmed value returns "Invalid login credentials".
        email: loginEmail.trim().toLowerCase(),
        password: loginPassword,
      });
      console.log(`[LOGIN-TIMING] signInWithPassword returned @ ${(performance.now() - t0).toFixed(0)}ms, error=${!!error}, user=${!!data?.user}`);

      if (error) {
        // If email isn't confirmed, guide user into verification step and auto-resend link
        if (String(error.message).toLowerCase().includes('email not confirmed')) {
          try {
            await supabase.auth.resend({
              type: 'signup',
              email: loginEmail,
              options: { emailRedirectTo: authRedirectUrl('/auth') },
            });
          } catch {}

          setUserEmail(loginEmail);
          setAuthStep('email-verification');

          toast({
            title: 'Email not confirmed',
            description: 'We sent you a fresh verification link. Please confirm and return here.',
          });
          setIsLoading(false);
          return;
        }
        throw error;
      }

      // ── BANDAGE: deterministic redirect ──
      localStorage.removeItem('lastActiveTab');
      sonnerToast.success('Welcome back!', { description: "You've successfully logged in.", duration: 5000 });

      if (data.session && data.user) {
        // Brief wait for AuthContext to pick up the session
        await new Promise(resolve => setTimeout(resolve, 500));

        // Fetch profile directly — don't depend on AuthContext profile
        const { data: profile } = await supabase
          .from('profiles')
          .select('onboarding_completed, user_type, documents_submitted')
          .eq('id', data.user.id)
          .maybeSingle();

        if (!profile?.onboarding_completed) {
          navigate('/auth?mode=signup&resume=1');
        } else {
          const dashboardPath = postLoginRoute(profile.user_type);
          navigate(dashboardPath);
        }
        return;
      }
      
    } catch (error: any) {
      const msg = String(error.message || '');
      const isBanned = msg.toLowerCase().includes('banned');
      toast({
        title: 'Login failed',
        description: isBanned
          ? 'This account is no longer active. Please contact support.'
          : error.message,
        variant: 'destructive',
      });
    } finally {
      console.log(`[LOGIN-TIMING] finally → isLoading=false @ ${(performance.now() - t0).toFixed(0)}ms`);
      setIsLoading(false);
    }
  };

  // Handle signup
  const handleSignup = async () => {
    // Role-first guard: account type must be chosen before creating an account
    if (!selectedRole) {
      toast({
        title: "Choose your role first",
        description: "Pick Broker, Carrier, Shipper, or Broker/Carrier before signing up.",
        variant: "destructive",
      });
      setAuthStep('pick-role');
      return;
    }

    // Validation
    if (!signupFullName.trim()) {
      toast({
        title: "Full name required",
        description: "Please enter your full name.",
        variant: "destructive",
      });
      return;
    }




    if (signupPassword !== signupConfirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure your passwords match.",
        variant: "destructive",
      });
      return;
    }

    if (!signupAcceptedTerms) {
      toast({
        title: "Accept terms",
        description: "Please accept the terms and conditions.",
        variant: "destructive",
      });
      return;
    }

    if (signupSmsConsent && !signupPhone) {
      toast({
        title: "Phone number required",
        description: "Please enter a phone number to receive SMS notifications.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    // Default to remember me = true for new signups (better UX)
    setRememberMePreference(true);

    // Arm Effect B's 2s manual-nav guard BEFORE awaiting signUp.
    // Otherwise, the SIGNED_IN event from supabase fires while we're still
    // awaiting and Effect B (or the searchParams effect) can clobber authStep
    // back to 'pick-role'.
    manualNavRef.current = Date.now();

    try {
      const [firstName, ...lastNameParts] = signupFullName.trim().split(' ');
      const lastName = lastNameParts.join(' ');
      
      // Combine country code and phone number
      const fullPhoneNumber = signupPhone ? `+1${signupPhone}` : '';

      // Sign up with Supabase
      const { data, error } = await supabase.auth.signUp({
        email: signupEmail,
        password: signupPassword,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
            phone: fullPhoneNumber,
            user_role: selectedRole, // pre-populate profiles.user_type at row creation
          },
          emailRedirectTo: authRedirectUrl('/auth'),
        }
      });

      if (error) throw error;

      // Check if user already exists (Supabase returns user with empty identities array)
      if (data.user && (!data.user.identities || data.user.identities.length === 0)) {
        toast({
          title: "Account already exists",
          description: "An account with this email already exists. Please log in instead.",
          variant: "destructive",
        });
        setActiveTab('login');
        setLoginEmail(signupEmail);
        return;
      }

      if (data.user) {
        // Bump manualNavRef FIRST so Effect B's 2s guard skips the post-signup re-run
        manualNavRef.current = Date.now();

        toast({
          title: "Account created!",
          description: "Please verify your phone to continue.",
        });
        
        // Set user info and move to phone verification step (phone first, then email)
        setUserId(data.user.id);
        setUserEmail(signupEmail);
        setUserPhone(fullPhoneNumber);
        setAuthStep('phone-verification');
        console.log('[signup-success] set authStep to phone-verification, userId:', data.user.id);

        // Cache signup progress so a refresh doesn't lose it
        saveSignupProgress({
          userId: data.user.id,
          email: signupEmail,
          phone: fullPhoneNumber,
          step: 'phone-verification',
        });
        
        // Note: signup form values intentionally preserved so the user can
        // hit Back from email verification (or later steps) and land on a
        // populated form rather than empty fields.
        setSignupPassword('');
        setSignupConfirmPassword('');

        // Affiliate attribution — fire-and-forget. MUST NOT block or break signup.
        try {
          const ref = getStoredRef();
          if (ref) {
            supabase.functions.invoke('attribute-referral', { body: { ref } })
              .then(() => clearStoredRef())
              .catch((e) => { console.warn('[affiliate] attribution failed (non-blocking)', e); });
          }
        } catch (e) {
          console.warn('[affiliate] attribution threw (non-blocking)', e);
        }

        // SMS consent record (A2P 10DLC compliance). Fire-and-forget; must not block signup.
        try {
          supabase.functions.invoke('record-signup-consent', {
            body: {
              user_id: data.user.id,
              sms_consent: signupSmsConsent,
              consent_text: signupSmsConsent
                ? 'I agree to receive SMS notifications from LaneRunner regarding my account, load activity, bids, quote requests, and other account notifications. Message frequency varies. Message and data rates may apply. Reply STOP to opt out, HELP for help.'
                : null,
            },
          }).catch((e) => { console.warn('[sms-consent] record failed (non-blocking)', e); });
        } catch (e) {
          console.warn('[sms-consent] invoke threw (non-blocking)', e);
        }
      }
    } catch (error: any) {
      toast({
        title: "Signup failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Check email verification status
  const checkEmailVerification = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user?.email_confirmed_at) {
        // Update profile
        await supabase
          .from('profiles')
          .update({ email_verified: true })
          .eq('id', user.id);

        setIsEmailVerified(true);

        toast({
          title: "Email verified!",
          description: "Moving to phone verification.",
        });
        
        setAuthStep('phone-verification');
      }
    } catch (error) {
      console.error('Error checking email verification:', error);
    }
  };

  // Send email verification code
  const handleSendEmailOtp = async () => {
    const targetEmail = userEmail || loginEmail || signupEmail;
    if (!targetEmail) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-verification-email', {
        body: { email: targetEmail },
      });
      
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      
      setEmailOtpSent(true);
      setCanResendEmailOtp(false);
      
      // Enable resend after 60 seconds
      setTimeout(() => setCanResendEmailOtp(true), 60000);
      
      toast({
        title: 'Code sent!',
        description: `Verification code sent to ${targetEmail}.`,
      });
    } catch (error: any) {
      console.error('Error sending verification email:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to send verification code',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Email auto-send REMOVED — email OTP fires only when user clicks "Send code".
  // Reset the auto-sent ref when leaving the email step so future visits work cleanly.
  useEffect(() => {
    if (authStep !== 'email-verification') {
      emailOtpAutoSentRef.current = false;
    }
  }, [authStep]);

  // Auto-send phone code when entering the phone verification step (phone is first now)
  // Requires a Turnstile token first — bypassed automatically if the site key isn't set.
  useEffect(() => {
    if (authStep !== 'phone-verification') {
      phoneOtpAutoSentRef.current = false;
      return;
    }
    if (!userPhone) return;
    if (generatedOtp) return;
    if (isLoading) return;
    if (phoneOtpAutoSentRef.current) return;
    if (turnstileToken === null) return; // wait for Turnstile

    phoneOtpAutoSentRef.current = true;
    handleSendOtp();
  }, [authStep, userPhone, generatedOtp, isLoading, turnstileToken]);

  // Verify email OTP code
  const handleVerifyEmailOtp = async () => {
    if (isEmailVerified) {
      toast({
        title: 'Email already verified',
        description: 'Continuing to authority verification.',
      });
      setAuthStep(
        selectedRole === 'shipper'
          ? 'onboarding-shipper-profile'
          : selectedRole === 'broker' || selectedRole === 'carrier' || selectedRole === 'broker_carrier'
          ? 'onboarding-fmcsa'
          : selectedRole
          ? 'onboarding-tier'
          : 'onboarding-role'
      );
      return;
    }

    const targetEmail = userEmail || loginEmail || signupEmail;
    if (!targetEmail || emailOtpCode.length !== 6) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('verify-email-otp', {
        body: { 
          email: targetEmail,
          otp_code: emailOtpCode,
          user_id: userId,
        },
      });
      
      if (error) {
        let msg = error.message;
        try {
          const body = await (error as any).context?.json?.();
          if (body?.error) msg = body.error;
        } catch { /* ignore */ }
        throw new Error(msg);
      }
      if (!data?.success) throw new Error(data?.error || 'Verification failed');
      
      toast({
        title: 'Email verified!',
        description: 'Continuing to authority verification.',
      });

      setIsEmailVerified(true);
      setEmailOtpCode('');
      
      setAuthStep(
        selectedRole === 'shipper'
          ? 'onboarding-shipper-profile'
          : selectedRole === 'broker' || selectedRole === 'carrier' || selectedRole === 'broker_carrier'
          ? 'onboarding-fmcsa'
          : selectedRole
          ? 'onboarding-tier'
          : 'onboarding-role'
      );
    } catch (error: any) {
      console.error('Error verifying email OTP:', error);
      const msg = error.message || 'Invalid verification code';
      const isExpired = msg.toLowerCase().includes('expired');
      
      if (isExpired) {
        toast({
          title: 'Code expired',
          description: 'Your verification code has expired. A new code is being sent.',
        });
        setEmailOtpCode('');
        // Auto-resend a new code
        handleSendEmailOtp();
      } else {
        toast({
          title: 'Verification failed',
          description: msg,
          variant: 'destructive',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Generate and send OTP
  const handleSendOtp = async () => {
    if (!userId || !userPhone) return;
    
    setIsLoading(true);
    try {
      // Generate 6-digit OTP
      const otp = Math.floor(100000 + Math.random() * 900000).toString();
      setGeneratedOtp(otp);
      
      // Store in database
      const expiresAt = new Date();
      expiresAt.setMinutes(expiresAt.getMinutes() + 10);
      
      const { error: dbError } = await supabase
        .from('phone_otps')
        .insert({
          user_id: userId,
          phone: userPhone,
          otp_code: otp,
          expires_at: expiresAt.toISOString(),
        });
      
      if (dbError) throw dbError;
      
      // Try to send SMS via Twilio edge function
      let smsSent = false;
      try {
        const { error: smsError } = await supabase.functions.invoke('send-sms-otp', {
          body: {
            phone: userPhone,
            otp: otp,
            turnstile_token: turnstileToken ?? '',
          },
        });
        
        if (!smsError) {
          smsSent = true;
          console.log('📱 OTP sent via SMS to:', userPhone);
        } else {
          console.warn('SMS send failed (likely Twilio trial account):', smsError);
        }
      } catch (smsError) {
        console.warn('SMS send failed:', smsError);
      }
      
      // Show appropriate message
      if (smsSent) {
        toast({
          title: "Code sent!",
          description: `Verification code sent to ${userPhone}`,
        });
    } else {
        toast({
          title: "Error",
          description: "Unable to send verification code. Please try again.",
          variant: "destructive",
        });
      }
      
      // Disable resend for 60 seconds
      setCanResendOtp(false);
      setTimeout(() => setCanResendOtp(true), 60000);

      // Single-use Turnstile token — reset widget for any resend.
      setTurnstileToken(null);
      turnstileRef.current?.reset();
      
    } catch (error: any) {
      console.error('Error sending OTP:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send verification code",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Verify OTP (server-side, with 5-attempt lockout)
  const handleVerifyOtp = async () => {
    if (!userId || !otpCode) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase.rpc('verify_phone_otp', {
        p_user_id: userId,
        p_otp_code: otpCode,
      });

      if (error) {
        throw new Error(error.message || 'Invalid or expired OTP');
      }

      const result = (data ?? {}) as { success?: boolean; error?: string; message?: string };

      if (!result.success) {
        throw new Error(result.message || 'Invalid or expired OTP');
      }

      toast({
        title: "Phone verified!",
        description: "Verify your email next.",
      });

      setIsPhoneVerified(true);
      setAuthStep('email-verification');
      setOtpCode('');
    } catch (error: any) {
      toast({
        title: "Verification failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle FMCSA code input changes with validation
  // FMCSA verification handlers removed — moved to VerifyMcGate (post-login).

  // Check if user is internal (@lanerunner.com)
  const isInternalUser = userEmail?.toLowerCase().endsWith('@lanerunner.com');

  // Handle internal user provisioning (skip Stripe)
  const handleInternalUserProvisioning = async () => {
    if (!userId || !selectedRole || !userEmail) return;
    
    setIsCreatingCheckout(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('provision-internal-user', {
        body: {
          userId,
          email: userEmail,
          userType: selectedRole,
          fmcsaData: verifiedFmcsaData,
          seats,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({
        title: 'Welcome to LaneRunner!',
        description: 'Your Pro account has been activated. Redirecting to dashboard...',
      });
      
      // Redirect to dashboard
      setTimeout(() => {
        navigate((selectedRole === 'broker' || selectedRole === 'broker_carrier') ? '/broker-dashboard' : selectedRole === 'shipper' ? '/shipper-dashboard' : '/carrier-dashboard');
      }, 1500);
    } catch (error: any) {
      console.error('Internal provisioning error:', error);
      toast({
        title: 'Activation failed',
        description: error.message || 'Unable to activate account. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingCheckout(false);
    }
  };

  // Handle Stripe checkout
  const handleStartCheckout = async (userTypeOverride?: string) => {
    if (!userId || !selectedRole || !userEmail) return;

    // App Store compliance: the native app never starts a purchase flow.
    if (!purchasesEnabled()) return;
    
    // Check if internal user - skip Stripe
    if (isInternalUser) {
      return handleInternalUserProvisioning();
    }
    
    setIsCreatingCheckout(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          email: userEmail,
          seats,
          userId,
          userType: userTypeOverride || selectedRole,
          fmcsaData: verifiedFmcsaData,
          successUrl: `${window.location.origin}/auth?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/auth?checkout=cancelled`,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      // Trial-abuse signal from server: prior subscription on this email → no trial
      if (data?.trialEligible === false) {
        toast({
          title: 'Welcome back!',
          description: "Free trial already used — full monthly rate applies. Redirecting to checkout…",
        });
      }

      // Redirect to Stripe checkout
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      toast({
        title: 'Checkout failed',
        description: error.message || 'Unable to start checkout. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingCheckout(false);
    }
  };

  // Shipper-only: save Industry + Monthly Volume to the user's primary company,
  // then advance to the tier-selection step. Called from onboarding-shipper-profile.
  const handleSubmitShipperProfile = async () => {
    if (!userId) return;
    if (!shipperIndustry || !shipperMonthlyVolume) return;
    setIsSavingShipperProfile(true);
    try {
      // Resolve the user's primary company (provisioning may not have created it yet
      // for paid flows; for free flows it doesn't exist until handleFreeTierProvisioning).
      const { data: membership } = await supabase
        .from('company_users')
        .select('company_id')
        .eq('user_id', userId)
        .eq('is_active', true)
        .order('is_primary', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (membership?.company_id) {
        const { error: updErr } = await supabase
          .from('companies')
          .update({
            industry: shipperIndustry,
            monthly_volume: shipperMonthlyVolume,
          })
          .eq('id', membership.company_id);
        if (updErr) throw updErr;
      } else {
        // No company yet — stash in sessionStorage so provisioning can pick it up later.
        try {
          sessionStorage.setItem(
            'lr_shipper_profile_v1',
            JSON.stringify({ industry: shipperIndustry, monthly_volume: shipperMonthlyVolume })
          );
        } catch {}
      }
      setAuthStep('onboarding-tier');
    } catch (err: any) {
      console.error('Shipper profile save error:', err);
      toast({
        title: 'Could not save your details',
        description: err?.message || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSavingShipperProfile(false);
    }
  };

  // Free-tier provisioning — creates placeholder company + free subscription, then routes to dashboard.
  // FMCSA verification happens post-login in VerifyMcGate, which also hydrates the placeholder company.
  const handleFreeTierProvisioning = async () => {
    if (!userId || !selectedRole) return;
    setIsProvisioningFree(true);
    try {
      const { data, error } = await supabase.functions.invoke('provision-free-account', {
        body: { userId, userType: selectedRole, seats: 1 },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      // If we stashed a shipper profile before the company existed, persist it now.
      if (selectedRole === 'shipper') {
        try {
          const raw = sessionStorage.getItem('lr_shipper_profile_v1');
          if (raw) {
            const stashed = JSON.parse(raw) as { industry?: string; monthly_volume?: string };
            const { data: membership } = await supabase
              .from('company_users')
              .select('company_id')
              .eq('user_id', userId)
              .eq('is_active', true)
              .order('is_primary', { ascending: false })
              .limit(1)
              .maybeSingle();
            if (membership?.company_id && (stashed.industry || stashed.monthly_volume)) {
              await supabase
                .from('companies')
                .update({
                  industry: stashed.industry ?? null,
                  monthly_volume: stashed.monthly_volume ?? null,
                })
                .eq('id', membership.company_id);
            }
            sessionStorage.removeItem('lr_shipper_profile_v1');
          }
        } catch (e) {
          console.warn('[shipper-profile post-provision write] non-fatal:', e);
        }
      }

      toast({
        title: 'Welcome to LaneRunner!',
        description: 'Your free account is ready. Verifying your authority next.',
      });

      // CRITICAL: refresh AuthContext so profile.subscription_active + companySubActive
      // reflect the newly-provisioned free subscription BEFORE we navigate into a
      // protected route. Without this, App.tsx's subscription gate sees stale state
      // and bounces the user to /reactivate.
      await refreshProfile();

      const dashPath =
        (selectedRole === 'broker' || selectedRole === 'broker_carrier') ? '/broker-dashboard' :
        selectedRole === 'shipper' ? '/shipper-dashboard' :
        '/carrier-dashboard';
      navigate(dashPath);
    } catch (err: any) {
      console.error('Free-tier provisioning error:', err);
      toast({
        title: 'Could not activate free account',
        description: err.message || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsProvisioningFree(false);
    }
  };

  useEffect(() => {
    const checkoutStatus = searchParams.get('checkout');
    const sessionId = searchParams.get('session_id');

    if (checkoutStatus === 'success' && sessionId) {
      // Verify the checkout session
      const verifySession = async () => {
        setIsLoading(true);
        try {
          const { data, error } = await supabase.functions.invoke('verify-checkout-session', {
            body: { sessionId },
          });

          if (error) throw error;

          if (data?.success) {
            toast({
              title: 'Welcome to LaneRunner!',
              description: 'Your subscription is active. Redirecting to dashboard...',
            });
            
            // Wait a moment for webhook to complete, then redirect
            setTimeout(() => {
              const dashPath = (selectedRole === 'broker' || selectedRole === 'broker_carrier') ? '/broker-dashboard' : selectedRole === 'shipper' ? '/shipper-dashboard' : '/carrier-dashboard';
              navigate(`${dashPath}?checkout=success`);
            }, 2000);
          } else {
            toast({
              title: 'Processing payment...',
              description: 'Please wait while we confirm your subscription.',
            });
            // Poll for completion
            setTimeout(() => {
              window.location.reload();
            }, 3000);
          }
        } catch (error: any) {
          console.error('Session verification error:', error);
          toast({
            title: 'Verification pending',
            description: 'Your payment is being processed. Please wait...',
          });
        } finally {
          setIsLoading(false);
        }
      };

      verifySession();
    } else if (checkoutStatus === 'cancelled') {
      toast({
        title: 'Checkout cancelled',
        description: 'You can try again when ready.',
      });
      setAuthStep('onboarding-tier');
      // Clear the URL params
      navigate('/auth', { replace: true });
    }
  }, [searchParams, navigate, selectedRole, toast]);

  const handleKeyDown = (e: React.KeyboardEvent, action: 'login' | 'signup' | 'resetPassword') => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (action === 'login') handleLogin();
      else if (action === 'signup') handleSignup();
    } else if (e.key === 'Escape') {
      if (showResetPasswordDialog) setShowResetPasswordDialog(false);
    }
  };

  // Progress steps: Role → Account → Email → Phone → FMCSA → Tier
  const getProgressStep = () => {
    switch (authStep) {
      case 'pick-role':
        return 0;
      case 'signup':
      case 'login':
        return 1;
      case 'email-verification':
        return 2;
      case 'phone-verification':
        return 3;
      case 'onboarding-role':
        return 3;
      case 'onboarding-fmcsa':
        return 4;
      case 'onboarding-shipper-profile':
      case 'onboarding-tier':
      case 'onboarding-plan':
        return 5;
      default:
        return 0;
    }
  };

  const progressStep = getProgressStep();

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // ── Pre-signup role picker (role-first flow) ──
  if (authStep === 'pick-role') {
    // Role lock: once phone or email is verified, the user has committed to a role.
    // Bounce them forward — they cannot switch roles after verification.
    if (isPhoneVerified || isEmailVerified) {
      setAuthStep(selectedRole ? 'signup' : (isEmailVerified ? 'email-verification' : 'phone-verification'));
      return null;
    }
    // Surface the tier param so we can highlight the corresponding chip
    // and show a "pre-selected" hint. Value is broker | carrier | shipper |
    // broker_carrier | lr_direct_private | lr_direct.
    const roleParam = (searchParams.get('role') || '').toLowerCase();
    const tierParam = (searchParams.get('tier') || '').toLowerCase();
    const preSelectedChipValue: string | null =
      roleParam === 'board_only' || tierParam === 'board_only' ? 'board_only'
      : roleParam === 'lr_direct_private' || tierParam === 'lr_direct_private' ? 'lr_direct_private'
      : roleParam === 'lr_direct' || tierParam === 'lr_direct' ? 'lr_direct'
      : roleParam || null;

    type RoleChip = {
      value: string; // Either selectedRole value, or lr_direct_* alias
      backingRole: 'broker' | 'carrier' | 'shipper' | 'broker_carrier';
      label: string; // Card title
      desc: string;  // Card description
      price: string; // Display price text only
      audience: 'shippers' | 'carriers' | 'brokers';
      icon: typeof Briefcase;
      tier?: 'board_only' | 'lr_direct_private' | 'lr_direct' | 'carrier' | 'carrier_board' | 'carrier_full';
      banner: string; // Top ribbon label (e.g. "LOAD BOARD", "FULL ACCESS")
      isFeatured?: boolean;
    };
    const roles: RoleChip[] = [
      {
        value: 'shipper',
        backingRole: 'shipper',
        label: 'Shipper',
        desc: 'Post freight, manage shipments, and work with your brokers.',
        price: 'Free',
        audience: 'shippers',
        icon: Package,
        banner: 'FREE ACCESS',
      },
      {
        value: 'carrier_board',
        backingRole: 'carrier',
        label: 'Load Board',
        desc: 'Search loads, post trucks, and manage your fleet on the LaneRunner board.',
        price: '$59.99/mo',
        audience: 'carriers',
        icon: Truck,
        tier: 'carrier_board',
        banner: 'LOAD BOARD',
      },
      {
        value: 'carrier_full',
        backingRole: 'carrier',
        label: 'Full Access',
        desc: 'Everything in Load Board + private shipper networks, open marketplace bidding, and premium tools.',
        price: '$99.99/mo',
        audience: 'carriers',
        icon: Sparkles,
        tier: 'carrier_full',
        banner: 'FULL ACCESS',
        isFeatured: true,
      },
      // Displayed price must equal the charged price: Load Board → board_only ($59.99).
      {
        value: 'board_only',
        backingRole: 'broker',
        label: 'Load Board',
        desc: 'Search, post, and manage freight on the LaneRunner load board.',
        price: '$59.99/mo',
        audience: 'brokers',
        icon: Briefcase,
        tier: 'board_only',
        banner: 'LOAD BOARD',
      },
      {
        value: 'lr_direct_private',
        backingRole: 'broker',
        label: 'Full Access',
        desc: 'Everything in Load Board + private shipper networks + the open marketplace.',
        price: '$99.99/mo',
        audience: 'brokers',
        icon: Sparkles,
        tier: 'lr_direct_private',
        banner: 'FULL ACCESS',
        isFeatured: true,
      },
      // Hidden: combo role kept in code but not rendered.
      {
        value: 'broker_carrier',
        backingRole: 'broker_carrier',
        label: 'Broker / Carrier',
        desc: 'Operate as both — broker loads and run trucks',
        price: '',
        audience: 'brokers',
        icon: Briefcase,
        banner: 'COMING SOON',
      },
    ];

    return (
      <div className="min-h-screen bg-white flex">
        <BrandPanel progressStep={0} />
        <div className="flex-1 flex items-center justify-center p-6 md:p-12">
          <div className="w-full max-w-lg">
            <div className="flex items-center gap-2 mb-8 md:hidden">
              <img src={lanerunnerLogoCyan} alt="LaneRunner" className="h-7" />
            </div>
            <button onClick={() => { manualNavRef.current = Date.now(); navigate('/'); }} className="flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-4 transition-colors">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <h2 className="text-2xl font-bold text-foreground mb-1">Create your account</h2>
            <p className="text-muted-foreground text-sm mb-6">
              First, tell us how you'll use LaneRunner. You'll see pricing for your role after sign-up.
            </p>
            {preSelectedChipValue && (
              <div className="mb-4 px-3 py-2 rounded-lg border border-[hsl(var(--brand-teal))]/30 bg-[hsl(var(--brand-teal))]/5 text-xs text-foreground">
                <span className="font-semibold text-[hsl(var(--brand-teal))]">Pre-selected</span> from your pricing choice — you can change it below.
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {roles
                .filter((role) => role.value !== 'broker_carrier')
                .map((role) => {
                const Icon = role.icon;
                const isDisabled = role.value === 'broker_carrier';
                const isHighlighted = preSelectedChipValue === role.value && !isDisabled;
                return (
                  <button
                    key={role.value}
                    type="button"
                    disabled={isDisabled}
                    aria-disabled={isDisabled}
                    onClick={() => {
                      if (isDisabled) return;
                      setSelectedRole(role.backingRole);
                      try { sessionStorage.setItem(SIGNUP_ROLE_KEY, role.backingRole); } catch {}
                      if (role.tier) {
                        try { sessionStorage.setItem('lr_signup_tier_v1', role.tier); } catch {}
                      }
                      setActiveTab('signup');
                      setAuthStep('signup');
                    }}
                    className={`relative border rounded-xl transition-all text-left group overflow-hidden ${
                      isDisabled
                        ? 'border-border opacity-60 cursor-not-allowed'
                        : role.isFeatured
                        ? 'hover:border-[hsl(var(--brand-teal))] hover:shadow-sm'
                        : 'hover:border-[hsl(var(--brand-teal))] hover:bg-[hsl(var(--brand-teal))]/5'
                    } ${
                      (selectedRole === role.backingRole && !role.tier && !isDisabled) || isHighlighted
                        ? 'border-[hsl(var(--brand-teal))] bg-[hsl(var(--brand-teal))]/5 ring-2 ring-[hsl(var(--brand-teal))]/40'
                        : role.isFeatured
                        ? 'border-[hsl(var(--brand-teal))]/40 bg-[hsl(var(--brand-teal))]/[0.03]'
                        : 'border-border'
                    }`}
                  >
                    {/* Top banner ribbon */}
                    <div
                      className={`w-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider ${
                        role.isFeatured
                          ? 'bg-[hsl(var(--brand-teal))] text-white'
                          : role.audience === 'shippers'
                          ? 'bg-[hsl(var(--brand-green))]/15 text-[hsl(var(--brand-green))]'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {role.banner}
                    </div>
                    <div className="p-4 pt-3">
                      <span
                        className={`inline-block mb-2 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border ${
                          role.audience === 'shippers'
                            ? 'bg-[hsl(var(--brand-green))]/15 text-[hsl(var(--brand-green))] border-[hsl(var(--brand-green))]/30'
                            : role.audience === 'carriers'
                            ? 'bg-[hsl(var(--brand-blue))]/15 text-[hsl(var(--brand-blue))] border-[hsl(var(--brand-blue))]/30'
                            : 'bg-[hsl(var(--brand-amber))]/15 text-[hsl(var(--brand-amber))] border-[hsl(var(--brand-amber))]/30'
                        }`}
                      >
                        For {role.audience}
                      </span>
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className="w-4 h-4 text-[hsl(var(--brand-teal))]" />
                        <h4 className="text-foreground font-semibold text-sm">{role.label}</h4>
                      </div>
                      <p className="text-foreground font-semibold text-sm mb-1">{role.price}</p>
                      <p className="text-muted-foreground text-xs leading-relaxed">{role.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="text-muted-foreground text-sm mt-6">
              Already have an account?{' '}
              <button
                onClick={() => handleTabChange('login')}
                className="text-[hsl(var(--brand-teal))] hover:underline font-medium"
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Email verification ──
  if (authStep === 'email-verification') {
    console.log('[email-verification] rendering, authStep:', authStep, 'isEmailVerified:', isEmailVerified);
    return (
      <div className="min-h-screen bg-white flex">
        <BrandPanel progressStep={progressStep} />
        <div className="flex-1 flex items-center justify-center p-6 md:p-12">
          <div className="w-full max-w-sm">
            {/* Mobile logo */}
            <div className="flex items-center gap-2 mb-8 md:hidden">
              <img src={lanerunnerLogoCyan} alt="LaneRunner" className="h-7" />
            </div>
            <h3 className="text-foreground font-semibold text-xl mb-2">Verify your email</h3>
            {!emailOtpSent ? (
              <p className="text-muted-foreground text-sm mb-6">We'll send a 6-digit code to {userEmail}. Click Send code to receive it.</p>
            ) : (
              <p className="text-muted-foreground text-sm mb-6">We sent a 6-digit code to {userEmail}. Enter it below.</p>
            )}
            <div className="bg-muted/50 rounded-lg p-4 mb-6 border border-border">
              <p className="text-foreground font-medium text-sm">Verification email</p>
              <p className="text-[hsl(var(--brand-teal))] text-sm">{userEmail}</p>
            </div>
            {!emailOtpSent ? (
              <Button
                onClick={handleSendEmailOtp}
                disabled={isLoading}
                className="w-full bg-[hsl(var(--brand-teal))] hover:bg-[hsl(var(--brand-teal))]/90 text-white font-medium py-5 rounded-lg disabled:opacity-50"
              >
                {isLoading ? 'Sending…' : 'Send code'}
              </Button>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="emailOtp" className="text-muted-foreground text-xs mb-2 block">6-digit code</Label>
                  <Input
                    id="emailOtp"
                    type="text"
                    value={emailOtpCode}
                    onChange={(e) => setEmailOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    className="text-center text-2xl tracking-[0.3em] border-border text-foreground focus:border-[hsl(var(--brand-teal))] rounded-lg py-4"
                  />
                </div>
                <Button
                  onClick={handleVerifyEmailOtp}
                  disabled={isLoading || emailOtpCode.length !== 6}
                  className="w-full bg-[hsl(var(--brand-teal))] hover:bg-[hsl(var(--brand-teal))]/90 text-white font-medium py-5 rounded-lg disabled:opacity-50"
                >
                  Verify & continue
                </Button>
                <Button
                  onClick={handleSendEmailOtp}
                  disabled={isLoading || !canResendEmailOtp}
                  variant="ghost"
                  className="w-full text-muted-foreground hover:text-foreground disabled:opacity-50"
                >
                  {canResendEmailOtp ? 'Resend code' : 'Wait 60s to resend'}
                </Button>
              </div>
            )}
            <button
              type="button"
              onClick={handleStartOver}
              className="block w-full text-center text-sm text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors mt-4"
            >
              Start over
            </button>
          </div>
        </div>
        {startOverDialog}
      </div>
    );
  }

  // ── Phone verification ──
  if (authStep === 'phone-verification') {
    return (
      <div className="min-h-screen bg-white flex">
        <BrandPanel progressStep={progressStep} />
        <div className="flex-1 flex items-center justify-center p-6 md:p-12">
          <div className="w-full max-w-sm">
            <div className="flex items-center gap-2 mb-8 md:hidden">
              <img src={lanerunnerLogoCyan} alt="LaneRunner" className="h-7" />
            </div>
            <h3 className="text-foreground font-semibold text-xl mb-2">Verify your phone</h3>
            <p className="text-muted-foreground text-sm mb-6">We sent a 6-digit code to {userPhone}.</p>
            <div className="bg-muted/50 rounded-lg p-4 mb-6 border border-border">
              <p className="text-foreground font-medium text-sm">Phone number</p>
              <p className="text-[hsl(var(--brand-teal))] text-sm">{userPhone}</p>
              {!generatedOtp && isLoading && (
                <p className="text-muted-foreground text-xs mt-2">Sending code…</p>
              )}
            </div>
            <TurnstileWidget
              ref={turnstileRef}
              className="mb-4 flex justify-center"
              onVerify={(t) => setTurnstileToken(t)}
              onExpire={() => setTurnstileToken(null)}
              onError={() => setTurnstileToken(null)}
            />
            <div className="space-y-4">
              <div>
                <Label htmlFor="otp" className="text-muted-foreground text-xs mb-2 block">6-digit code</Label>
                <Input
                  id="otp"
                  type="text"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  className="text-center text-2xl tracking-[0.3em] border-border text-foreground focus:border-[hsl(var(--brand-teal))] rounded-lg py-4"
                />
              </div>
              <Button
                onClick={handleVerifyOtp}
                disabled={isLoading || otpCode.length !== 6 || !generatedOtp}
                className="w-full bg-[hsl(var(--brand-teal))] hover:bg-[hsl(var(--brand-teal))]/90 text-white font-medium py-5 rounded-lg disabled:opacity-50"
              >
                Verify & continue
              </Button>
              <Button
                onClick={handleSendOtp}
                disabled={isLoading || !canResendOtp || !turnstileToken}
                variant="ghost"
                className="w-full text-muted-foreground hover:text-foreground disabled:opacity-50"
              >
                {canResendOtp ? 'Resend code' : 'Wait 60s to resend'}
              </Button>
            </div>
            <button
              type="button"
              onClick={handleStartOver}
              className="block w-full text-center text-sm text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors mt-4"
            >
              Start over
            </button>
          </div>
        </div>
        {startOverDialog}
      </div>
    );
  }

  // ── Role selection ──
  if (authStep === 'onboarding-role') {
    return (
      <div className="min-h-screen bg-white flex">
        <BrandPanel progressStep={progressStep} />
        <div className="flex-1 flex items-center justify-center p-6 md:p-12">
          <div className="w-full max-w-sm">
            <div className="flex items-center gap-2 mb-8 md:hidden">
              <img src={lanerunnerLogoCyan} alt="LaneRunner" className="h-7" />
            </div>
            <button onClick={() => { manualNavRef.current = Date.now(); setAuthStep('phone-verification'); }} className="flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-4 transition-colors">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            {clockDriftMinutes !== null && clockDriftMinutes > 2 && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  LaneRunner requires your device's clock to match real-world time for security. If you see a clock sync message, your device's date or time is incorrect. Most fixes take less than 30 seconds.{' '}
                  <a href="/clock-help" target="_blank" rel="noopener noreferrer" className="underline font-medium">Need help? Step-by-step instructions</a>
                </AlertDescription>
              </Alert>
            )}
            <h3 className="text-foreground font-semibold text-xl mb-2">Your role</h3>
            <p className="text-muted-foreground text-sm mb-6">Select how you'll use LaneRunner</p>
            <div className="grid grid-cols-2 gap-3">
              {([
                { value: 'broker' as const, label: 'Broker', desc: 'Post, search, manage, price loads', icon: Briefcase },
                { value: 'shipper' as const, label: 'Shipper', desc: 'List freight, track orders, manage shipments', icon: Package },
                { value: 'carrier' as const, label: 'Carrier', desc: 'Find brokered loads, book with brokers', icon: Truck },
                { value: 'broker_carrier' as const, label: 'Broker / Carrier', desc: 'Operate as both — broker loads and run trucks', icon: Briefcase },
              ]).map((role) => {
                const isDisabled = role.value === 'broker_carrier';
                const showBanner = isDisabled;
                return (
                  <button
                    key={role.value}
                    type="button"
                    disabled={isDisabled}
                    aria-disabled={isDisabled}
                    onClick={() => {
                      if (isDisabled) return;
                      setSelectedRole(role.value);
                      setAuthStep(
                        role.value === 'shipper'
                          ? 'onboarding-tier'
                          : 'onboarding-fmcsa'
                      );
                    }}
                    className={`relative p-4 border rounded-xl transition-all text-left group ${
                      isDisabled
                        ? 'border-border opacity-60 cursor-not-allowed'
                        : 'hover:border-[hsl(var(--brand-teal))] hover:bg-[hsl(var(--brand-teal))]/5'
                    } ${selectedRole === role.value && !isDisabled ? 'border-[hsl(var(--brand-teal))] bg-[hsl(var(--brand-teal))]/5' : 'border-border'}`}
                  >
                    {showBanner && (
                      <span className="absolute top-2 right-2 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider text-black border border-black/10" style={{ backgroundColor: '#F7CF0D' }}>
                        Coming Soon
                      </span>
                    )}
                    <h4 className="text-foreground font-semibold text-sm mb-1">{role.label}</h4>
                    <p className="text-muted-foreground text-xs leading-relaxed">{role.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── FMCSA verification step (broker / carrier only) ──
  if (authStep === 'onboarding-fmcsa') {
    const fmcsaRole =
      selectedRole === 'broker' || selectedRole === 'carrier' || selectedRole === 'broker_carrier'
        ? selectedRole
        : 'broker';
    return (
      <div className="min-h-screen bg-white flex">
        <BrandPanel progressStep={progressStep} />
        <div className="flex-1 flex items-center justify-center p-6 md:p-12">
          <div className="w-full max-w-md">
            <div className="flex items-center gap-2 mb-8 md:hidden">
              <img src={lanerunnerLogoCyan} alt="LaneRunner" className="h-7" />
            </div>
            {userId ? (
              <SignupFmcsaStep
                userId={userId}
                role={fmcsaRole}
                onBack={() => {
                  manualNavRef.current = Date.now();
                  setAuthStep('email-verification');
                }}
                onVerified={() => {
                  saveSignupProgress({
                    userId,
                    email: userEmail || signupEmail,
                    phone: userPhone,
                    step: 'onboarding-tier',
                  });
                  setAuthStep('onboarding-tier');
                }}
              />
            ) : (
              <div className="text-sm text-muted-foreground">Loading…</div>
            )}
          </div>
        </div>
      </div>
    );
  }


  // ── Shipper-only profile step (Industry + Monthly Load Volume) ──
  if (authStep === 'onboarding-shipper-profile') {
    const canContinue = !!shipperIndustry && !!shipperMonthlyVolume && !isSavingShipperProfile;
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            <button
              onClick={() => { manualNavRef.current = Date.now(); setAuthStep('phone-verification'); }}
              className="flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-4 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <h2 className="text-foreground font-bold text-2xl mb-2">Tell us about your operation</h2>
            <p className="text-muted-foreground text-sm mb-6">
              A couple of quick details help us tailor your shipper dashboard and broker network.
            </p>

            <div className="space-y-5">
              <div>
                <Label className="text-muted-foreground text-xs mb-2 block">Industry</Label>
                <Select value={shipperIndustry} onValueChange={setShipperIndustry}>
                  <SelectTrigger className="border-border text-foreground focus:border-[hsl(var(--brand-teal))] rounded-lg py-5">
                    <SelectValue placeholder="Select your freight type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Food">Food</SelectItem>
                    <SelectItem value="Manufacturing">Manufacturing</SelectItem>
                    <SelectItem value="Retail">Retail</SelectItem>
                    <SelectItem value="Agricultural">Agricultural</SelectItem>
                    <SelectItem value="Industrial">Industrial</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-muted-foreground text-xs mb-2 block">Estimated monthly load volume</Label>
                <Select value={shipperMonthlyVolume} onValueChange={setShipperMonthlyVolume}>
                  <SelectTrigger className="border-border text-foreground focus:border-[hsl(var(--brand-teal))] rounded-lg py-5">
                    <SelectValue placeholder="Select a volume range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-10">1–10 loads / month</SelectItem>
                    <SelectItem value="11-50">11–50 loads / month</SelectItem>
                    <SelectItem value="51-100">51–100 loads / month</SelectItem>
                    <SelectItem value="100+">100+ loads / month</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={handleSubmitShipperProfile}
                disabled={!canContinue}
                className="w-full bg-[hsl(var(--brand-teal))] hover:bg-[hsl(var(--brand-teal))]/90 text-white font-medium py-5 rounded-lg disabled:opacity-50"
              >
                {isSavingShipperProfile ? 'Saving…' : 'Continue'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Tier selection (free vs premium) ──
  if (authStep === 'onboarding-tier') {
    if (!selectedRole) {
      setAuthStep('pick-role');
      return null;
    }

    // Honor tier pre-selection from pricing page (URL ?tier= or sessionStorage).
    // Skip the Free-vs-Premium choice when user already picked on /pricing.
    const preselectedTier = (() => {
      try {
        return (
          (searchParams.get('tier') || '').toLowerCase() ||
          (sessionStorage.getItem('lr_signup_tier_v1') || '').toLowerCase()
        );
      } catch {
        return (searchParams.get('tier') || '').toLowerCase();
      }
    })();

    // Shippers have no paid tier — skip the picker entirely and auto-provision Free.
    if (selectedRole === 'shipper') {
      if (!tierAutoRouteRef.current) {
        tierAutoRouteRef.current = true;
        handleFreeTierProvisioning();
      }
      return (
        <div className="min-h-screen bg-white flex items-center justify-center">
          <div className="text-muted-foreground text-sm">Activating your account…</div>
        </div>
      );
    }

    // Free broker tier retired. The $249.99 "LR Direct Full" tier is
    // discontinued — brokers pick Load Board ($59.99) or Full Access ($99.99).

    if (preselectedTier && !tierAutoRouteRef.current) {
      tierAutoRouteRef.current = true;
      // Discontinued $249.99 tier: legacy links land on Full Access ($99.99)
      // instead of the removed tier. Checkout for 'lr_direct' is also rejected
      // server-side in create-checkout-session.
      if (preselectedTier === 'lr_direct' || preselectedTier === 'lr_direct_monthly') {
        handleStartCheckout('lr_direct_private');
        return (
          <div className="min-h-screen bg-white flex items-center justify-center">
            <div className="text-muted-foreground text-sm">Redirecting to checkout…</div>
          </div>
        );
      }
      if (preselectedTier === 'lr_direct_yearly') {
        handleStartCheckout('broker_yearly');
        return (
          <div className="min-h-screen bg-white flex items-center justify-center">
            <div className="text-muted-foreground text-sm">Redirecting to checkout…</div>
          </div>
        );
      }
      if (preselectedTier === 'lr_direct_private') {
        // LR Direct Private — $99.99/mo (LaneRunner Broker Pro).
        handleStartCheckout('lr_direct_private');
        return (
          <div className="min-h-screen bg-white flex items-center justify-center">
            <div className="text-muted-foreground text-sm">Redirecting to checkout…</div>
          </div>
        );
      }
      if (preselectedTier === 'board_only') {
        // Load Board only — $59.99/mo.
        handleStartCheckout('board_only');
        return (
          <div className="min-h-screen bg-white flex items-center justify-center">
            <div className="text-muted-foreground text-sm">Redirecting to checkout…</div>
          </div>
        );
      }
      if (preselectedTier === 'board_only_yearly') {
        handleStartCheckout('board_only_yearly');
        return (
          <div className="min-h-screen bg-white flex items-center justify-center">
            <div className="text-muted-foreground text-sm">Redirecting to checkout…</div>
          </div>
        );
      }
      // Carrier tiers (v5): Load Board $59.99/mo · $575.90/yr,
      // Full Access $99.99/mo · $959.90/yr.
      if (
        preselectedTier === 'carrier_board' ||
        preselectedTier === 'carrier_board_yearly' ||
        preselectedTier === 'carrier_full' ||
        preselectedTier === 'carrier_full_yearly'
      ) {
        handleStartCheckout(preselectedTier);
        return (
          <div className="min-h-screen bg-white flex items-center justify-center">
            <div className="text-muted-foreground text-sm">Redirecting to checkout…</div>
          </div>
        );
      }
      if (preselectedTier === 'carrier_yearly') {
        // Legacy carrier yearly link → new Load Board yearly ($575.90/yr).
        handleStartCheckout('carrier_board_yearly');
        return (
          <div className="min-h-screen bg-white flex items-center justify-center">
            <div className="text-muted-foreground text-sm">Redirecting to checkout…</div>
          </div>
        );
      }
      if (preselectedTier === 'carrier_monthly' || preselectedTier === 'carrier') {
        // Legacy carrier monthly link → new Load Board monthly ($59.99/mo).
        handleStartCheckout('carrier_board');
        return (
          <div className="min-h-screen bg-white flex items-center justify-center">
            <div className="text-muted-foreground text-sm">Redirecting to checkout…</div>
          </div>
        );
      }
      if (preselectedTier === 'premium-yearly' || preselectedTier === 'broker_yearly') {
        // Premium Yearly — broker $959.90/yr, carrier Full Access $959.90/yr
        handleStartCheckout(
          selectedRole === 'carrier' ? 'carrier_full_yearly'
          : selectedRole === 'broker' ? 'broker_yearly'
          : (selectedRole || 'broker_yearly')
        );
        return (
          <div className="min-h-screen bg-white flex items-center justify-center">
            <div className="text-muted-foreground text-sm">Redirecting to checkout…</div>
          </div>
        );
      }

      if (preselectedTier === 'premium' || preselectedTier === 'premium-monthly') {
        setAuthStep('onboarding-plan');
        return null;
      }
      // Free preselect no longer honored for paid roles — fall through to picker.
    }

    const roleLabel =
      selectedRole === 'broker_carrier' ? 'Broker / Carrier' :
      selectedRole.charAt(0).toUpperCase() + selectedRole.slice(1);
    // Displayed price must match the Stripe price the checkout charges.
    const premiumPrice =
      selectedRole === 'broker' ? 99.99 :
      selectedRole === 'broker_carrier' ? 149.99 :
      39.99;
    const isBroker = selectedRole === 'broker';

    return (
      <div className="min-h-screen bg-white flex">
        <BrandPanel progressStep={progressStep} />
        <div className="flex-1 flex items-center justify-center p-6 md:p-12">
          <div className="w-full max-w-md">
            <div className="flex items-center gap-2 mb-8 md:hidden">
              <img src={lanerunnerLogoCyan} alt="LaneRunner" className="h-7" />
            </div>
            <button
              onClick={() => { manualNavRef.current = Date.now(); setAuthStep('phone-verification'); }}
              className="flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-4 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" /> Back
            </button>

            <h3 className="text-foreground font-semibold text-xl mb-1">Choose your plan</h3>
            <p className="text-muted-foreground text-sm mb-6">
              You can upgrade or downgrade anytime — {roleLabel} accounts.
            </p>

            <div className="space-y-3">
              {/* Premium tier */}
              <button
                type="button"
                onClick={() => setSelectedTier('premium')}
                className={`w-full text-left p-5 border rounded-xl transition-all relative ${
                  selectedTier === 'premium'
                    ? 'border-[hsl(var(--brand-teal))] bg-[hsl(var(--brand-teal))]/5'
                    : 'border-border hover:border-[hsl(var(--brand-teal))]/50'
                }`}
              >
                <div className="flex items-baseline justify-between mb-1">
                  <h4 className="text-foreground font-semibold">Premium</h4>
                  <span className="text-foreground font-semibold">${premiumPrice}<span className="text-xs text-muted-foreground font-normal">/seat/mo</span></span>
                </div>
                <p className="text-muted-foreground text-xs mb-3">No ads, monthly credits, full toolkit.</p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li className="flex items-start gap-1.5"><CheckCircle className="w-3 h-3 mt-0.5 text-[hsl(var(--brand-teal))]" /> Post and search loads</li>
                  <li className="flex items-start gap-1.5"><CheckCircle className="w-3 h-3 mt-0.5 text-[hsl(var(--brand-teal))]" /> FMCSA-verified network</li>
                  <li className="flex items-start gap-1.5"><CheckCircle className="w-3 h-3 mt-0.5 text-[hsl(var(--brand-teal))]" /> Monthly free credits & team seats</li>
                </ul>
              </button>

              {/* $249.99 LR Direct Full tier discontinued — no longer offered. */}
            </div>

            <Button
              onClick={() => {
                if (selectedTier === 'premium') return setAuthStep('onboarding-plan');
              }}
              disabled={!selectedTier || isCreatingCheckout}
              className="w-full bg-[hsl(var(--brand-teal))] hover:bg-[hsl(var(--brand-teal))]/90 text-white font-medium py-5 rounded-lg mt-5 disabled:opacity-50"
            >
              {isCreatingCheckout ? 'Redirecting…' : 'Continue to checkout'}
            </Button>

            <p className="text-center text-muted-foreground text-xs mt-4">
              You'll verify your MC/DOT authority right after this.
            </p>
          </div>
        </div>
      </div>
    );
  }



  // ── Plan selection ──
  if (authStep === 'onboarding-plan') {
    // Hard-gate: never render pricing without a confirmed role. This prevents the
    // user from ever seeing a "default" plan before role selection.
    if (!selectedRole) {
      setAuthStep('pick-role');
      return null;
    }
    const ROLE_PRICING: Record<string, { price: number }> = {
      broker: { price: 99.99 },
      carrier: { price: 79.99 },
      shipper: { price: 79.99 },
      broker_carrier: { price: 149.99 },
    };
    const pricePerSeat = ROLE_PRICING[selectedRole].price;
    const subtotal = pricePerSeat * seats;
    const totalPrice = subtotal.toFixed(2);

    const features = [
      'Unlimited load postings',
      'Real-time rate analytics',
      'Team collaboration tools',
      'Priority 24/7 support',
    ];

    return (
      <div className="min-h-screen bg-white flex">
        <BrandPanel progressStep={progressStep} />
        <div className="flex-1 flex items-center justify-center p-6 md:p-12">
          <div className="w-full max-w-sm">
            <div className="flex items-center gap-2 mb-8 md:hidden">
              <img src={lanerunnerLogoCyan} alt="LaneRunner" className="h-7" />
            </div>
            <button onClick={() => { manualNavRef.current = Date.now(); setAuthStep('onboarding-tier'); }} className="flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-4 transition-colors">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            {clockDriftMinutes !== null && clockDriftMinutes > 2 && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  LaneRunner requires your device's clock to match real-world time for security. If you see a clock sync message, your device's date or time is incorrect. Most fixes take less than 30 seconds.{' '}
                  <a href="/clock-help" target="_blank" rel="noopener noreferrer" className="underline font-medium">Need help? Step-by-step instructions</a>
                </AlertDescription>
              </Alert>
            )}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-foreground font-semibold text-xl">Order summary</h3>
            </div>

            {/* Plan Row */}
            <div className="flex items-center justify-between py-3 border-b border-border">
              <div>
                <p className="text-foreground text-sm">Pro plan</p>
                <p className="text-muted-foreground text-xs">per seat / month</p>
              </div>
              <p className="text-foreground text-sm font-medium">${pricePerSeat}</p>
            </div>

            {/* Seats Row */}
            <div className="flex items-center justify-between py-3 border-b border-border">
              <p className="text-muted-foreground text-sm">{seats} {seats === 1 ? 'user' : 'users'}</p>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setSeats(Math.max(1, seats - 1))}
                  disabled={seats <= 1}
                  className="w-6 h-6 text-muted-foreground hover:text-foreground disabled:opacity-40"
                >
                  <Minus className="w-3 h-3" />
                </Button>
                <span className="text-foreground w-4 text-center text-sm">{seats}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setSeats(Math.min(100, seats + 1))}
                  disabled={seats >= 100}
                  className="w-6 h-6 text-muted-foreground hover:text-foreground disabled:opacity-40"
                >
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
            </div>

            {/* Subtotal */}
            <div className="flex items-center justify-between py-3 text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="text-muted-foreground">${subtotal.toFixed(2)}</span>
            </div>

            {/* Total */}
            {isInternalUser ? (
              <>
                <div className="flex items-center justify-between py-3 border-t border-border">
                  <span className="text-foreground font-medium">Internal Account</span>
                  <span className="text-[hsl(var(--brand-teal))] text-xl font-semibold">FREE</span>
                </div>
                <p className="text-right text-[hsl(var(--brand-teal))] text-xs mt-2">
                  @lanerunner.com users get Pro access at no cost
                </p>
                <Button
                  onClick={() => handleStartCheckout()}
                  disabled={isCreatingCheckout}
                  className="w-full bg-[hsl(var(--brand-teal))] hover:bg-[hsl(var(--brand-teal))]/90 text-white font-medium py-5 rounded-lg mt-4"
                >
                  {isCreatingCheckout ? 'Activating...' : 'Activate Pro Account'}
                </Button>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between py-3 border-t border-border">
                  <span className="text-foreground font-medium">Total</span>
                  <span className="text-foreground text-xl font-semibold">${totalPrice}</span>
                </div>
                <p className="text-right text-[hsl(var(--brand-teal))] text-xs mt-2">
                  Billed monthly • Cancel anytime
                </p>
                <Button
                  onClick={() => handleStartCheckout()}
                  disabled={isCreatingCheckout}
                  className="w-full bg-[hsl(var(--brand-teal))] hover:bg-[hsl(var(--brand-teal))]/90 text-white font-medium py-5 rounded-lg mt-4"
                >
                  {isCreatingCheckout ? 'Processing...' : 'Subscribe'}
                </Button>
                <p className="text-center text-muted-foreground text-xs mt-4">
                  By continuing, you accept our{' '}
                  <a href="/terms" className="underline hover:text-foreground">Terms and Conditions</a>.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Main login / signup ──
  return (
    <div className="min-h-screen bg-white flex">
      {/* Left brand panel */}
      <div className="hidden md:flex md:w-[20%] md:max-w-[20%] bg-[#0c1220] flex-col justify-between p-10 text-white">
        <div>
          <div className="flex items-center gap-2 mb-16">
            <img src={lanerunnerLogoCyan} alt="LaneRunner" className="h-8" />
          </div>
          <p className="text-xs font-semibold uppercase tracking-wider text-white/70 mb-3">
            Freight Sourcing &amp; Shipment Management
          </p>
          <h1 className="text-3xl font-bold leading-tight mb-3">
            One Platform. Total Freight Clarity.
          </h1>
          <p className="text-sm text-white/40 leading-relaxed max-w-xs">
            The all-in-one platform that helps shippers source capacity faster, compare every quote in one place, and manage each shipment from award to delivery.
          </p>
        </div>
        <p className="text-white/30 text-xs">© 2026 LaneRunner, Inc.</p>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 md:hidden">
            <img src={lanerunnerLogoCyan} alt="LaneRunner" className="h-7" />
          </div>


          <h2 className="text-2xl font-bold text-foreground mb-1">
            {activeTab === 'login' ? 'Sign in' : 'Create your account'}
          </h2>
          {!isNativePlatform() && (
            <p className="text-muted-foreground text-sm mb-2">
              {activeTab === 'login' 
                ? <>Don't have an account?{' '}<button onClick={() => handleTabChange('signup')} className="text-[hsl(var(--brand-teal))] hover:underline font-medium">Sign up</button></>
                : <>Already have an account?{' '}<button onClick={() => handleTabChange('login')} className="text-[hsl(var(--brand-teal))] hover:underline font-medium">Sign in</button></>
              }
            </p>
          )}
          {activeTab === 'signup' && selectedRole && (
            <div className="mb-6">
              <div className="inline-flex items-center gap-2 rounded-full border-2 border-primary bg-background py-1.5 pl-4 pr-3 shadow-sm">
                <span className="text-sm font-semibold text-foreground">Signing up as</span>
                <span className="rounded-full bg-lr-yellow px-3 py-1 text-lg font-extrabold text-warning-foreground">
                  {selectedRole === 'broker_carrier' ? 'Broker / Carrier' : selectedRole.charAt(0).toUpperCase() + selectedRole.slice(1)}
                </span>
                <span className="text-muted-foreground">·</span>
                <button
                  onClick={() => { try { sessionStorage.removeItem(SIGNUP_ROLE_KEY); } catch {}; setSelectedRole(null); setAuthStep('pick-role'); }}
                  className="text-sm font-bold text-primary hover:underline"
                >
                  change
                </button>
              </div>
              {selectedRole === 'shipper' && (
                <p className="mt-2 text-sm text-muted-foreground">
                  Shipper accounts are reviewed and verified before activation.
                </p>
              )}
            </div>
          )}
          {activeTab === 'signup' && !selectedRole && (
            <div className="mb-6">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Please pick your account type first.{' '}
                  <button onClick={() => setAuthStep('pick-role')} className="underline font-medium text-[hsl(var(--brand-teal))]">Choose role</button>
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/* Clock drift warning - visible across all auth states */}
          {clockDriftMinutes !== null && clockDriftMinutes > 2 && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                  LaneRunner requires your device's clock to match real-world time for security. If you see a clock sync message, your device's date or time is incorrect. Most fixes take less than 30 seconds.{' '}
                  <a href="/clock-help" target="_blank" rel="noopener noreferrer" className="underline font-medium">Need help? Step-by-step instructions</a>
                </AlertDescription>
            </Alert>
          )}

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-muted-foreground">{activeTab === 'login' ? 'sign in with email' : 'create account'}</span></div>
          </div>

          <Tabs value={activeTab} onValueChange={(v) => handleTabChange(v as 'login' | 'signup')} className="w-full">
            {/* Native (Capacitor) hides the signup tab — App Store / Play Store policy
                prefers account creation on the web. On web, isNativePlatform() is
                always false, so the 2-tab layout is preserved exactly. */}
            {isNativePlatform() ? (
              <TabsList className="grid grid-cols-1 w-full mb-6">
                <TabsTrigger value="login">Login</TabsTrigger>
              </TabsList>
            ) : (
              <TabsList className="grid grid-cols-2 w-full mb-6">
                <TabsTrigger value="login">Login</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
              </TabsList>
            )}

            <TabsContent value="login">
              <div className="space-y-4" onKeyDown={(e) => handleKeyDown(e, 'login')}>
                <div className="space-y-2">
                  <Label htmlFor="login_email" className="text-foreground text-sm font-medium">Email</Label>
                  <Input
                    id="login_email"
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="border-border text-foreground placeholder:text-muted-foreground focus:border-[hsl(var(--brand-teal))] focus:ring-2 focus:ring-[hsl(var(--brand-teal))]/20 rounded-lg py-5"
                    autoComplete="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    inputMode="email"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="login_password" className="text-foreground text-sm font-medium">Password</Label>
                  <div className="relative">
                    <Input
                      id="login_password"
                      type={showLoginPassword ? "text" : "password"}
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      placeholder="••••••••"
                      className="border-border text-foreground placeholder:text-muted-foreground focus:border-[hsl(var(--brand-teal))] focus:ring-2 focus:ring-[hsl(var(--brand-teal))]/20 rounded-lg py-5 pr-12"
                      autoComplete="current-password"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-4 hover:bg-transparent text-muted-foreground hover:text-foreground"
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                    >
                      {showLoginPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </Button>
                  </div>
                </div>

                <Button
                  onClick={handleLogin}
                  disabled={isLoading}
                  className="w-full bg-[hsl(var(--brand-teal))] hover:bg-[hsl(var(--brand-teal))]/90 text-white font-semibold py-5 rounded-lg transition-all mt-4"
                >
                  {isLoading ? 'Signing in…' : 'Sign in'}
                </Button>

                <div className="flex items-center justify-center space-x-2 mt-4">
                  <Checkbox
                    id="remember_me"
                    checked={rememberMe}
                    onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                    className="border-border data-[state=checked]:bg-[hsl(var(--brand-teal))] data-[state=checked]:border-[hsl(var(--brand-teal))]"
                  />
                  <Label htmlFor="remember_me" className="text-sm text-muted-foreground cursor-pointer font-normal">
                    Keep me logged in on this device for 30 days
                  </Label>
                </div>

                <div className="text-center">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (isNativePlatform()) {
                        openExternal('https://lanerunner.com/forgot-password');
                      } else {
                        navigate('/forgot-password');
                      }
                    }}
                    className="text-sm text-[hsl(var(--brand-teal))] hover:text-[hsl(var(--brand-teal))]/80 hover:bg-transparent p-0 h-auto font-medium"
                  >
                    Forgot password?
                  </Button>
                </div>

                {isNativePlatform() && (
                  <p className="text-xs text-muted-foreground text-center">
                    Sign up at lanerunner.com
                  </p>
                )}

                <p className="text-xs text-muted-foreground text-center">
                  Having issues? Please email{' '}
                  <a href="mailto:support@lanerunner.com" className="text-[hsl(var(--brand-teal))] hover:underline">
                    support@lanerunner.com
                  </a>
                </p>
              </div>
            </TabsContent>

            <TabsContent value="signup">
              <div className="space-y-4" onKeyDown={(e) => handleKeyDown(e, 'signup')}>
                <div className="space-y-2">
                  <Label htmlFor="signup_name" className="text-foreground text-sm font-medium">Full Name</Label>
                  <Input
                    id="signup_name"
                    type="text"
                    value={signupFullName}
                    onChange={(e) => setSignupFullName(e.target.value)}
                    placeholder="Enter your full name"
                    className="border-border text-foreground placeholder:text-muted-foreground focus:border-[hsl(var(--brand-teal))] focus:ring-2 focus:ring-[hsl(var(--brand-teal))]/20 rounded-lg py-5"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup_email" className="text-foreground text-sm font-medium">Business Email</Label>
                  <Input
                    id="signup_email"
                    type="email"
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="border-border text-foreground placeholder:text-muted-foreground focus:border-[hsl(var(--brand-teal))] focus:ring-2 focus:ring-[hsl(var(--brand-teal))]/20 rounded-lg py-5"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="signup_phone" className="text-foreground text-sm font-medium">Mobile Phone (U.S. only)</Label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-border bg-muted text-muted-foreground text-sm font-medium select-none">
                      +1
                    </span>
                    <Input
                      id="signup_phone"
                      type="tel"
                      value={formatPhoneDisplay(signupPhone)}
                      onChange={handlePhoneChange}
                      placeholder="(312) 555-8843"
                      className="flex-1 border-border text-foreground placeholder:text-muted-foreground focus:border-[hsl(var(--brand-teal))] focus:ring-2 focus:ring-[hsl(var(--brand-teal))]/20 rounded-l-none rounded-r-lg py-5"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Use a valid U.S. mobile number to receive your verification code.</p>
                </div>

                <div className="rounded-lg border border-border/60 bg-muted/30 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--brand-teal))]">Optional</span>
                    <span className="text-xs text-muted-foreground">You can skip this and still create your account</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="sms_consent"
                      checked={signupSmsConsent}
                      onCheckedChange={(checked) => setSignupSmsConsent(checked as boolean)}
                      className="border-border data-[state=checked]:bg-[hsl(var(--brand-teal))] data-[state=checked]:border-[hsl(var(--brand-teal))] mt-1"
                    />
                    <div className="flex-1">
                      <Label
                        htmlFor="sms_consent"
                        className="text-sm text-foreground cursor-pointer font-normal leading-relaxed"
                      >
                        <span className="font-semibold">(Optional)</span> I agree to receive SMS notifications from LaneRunner regarding my account, load activity, bids, quote requests, and other account notifications. Message frequency varies. Message and data rates may apply. Reply STOP to opt out, HELP for help.
                      </Label>
                      <p className="text-xs text-muted-foreground/80 mt-1.5 leading-relaxed">
                        <strong>Consent is not required</strong> to create an account or use LaneRunner. Leave this box unchecked to decline SMS messages and continue using the platform normally. By checking this box, you consent to receive SMS messages from LaneRunner related to your account and activity. Message frequency varies. Message and data rates may apply. Reply STOP to cancel or HELP for help.
                      </p>
                    </div>
                  </div>
                </div>


                <div className="space-y-2">
                  <Label htmlFor="signup_password" className="text-foreground text-sm font-medium">Password</Label>
                  <div className="relative">
                    <Input
                      id="signup_password"
                      type={showSignupPassword ? "text" : "password"}
                      value={signupPassword}
                     onChange={(e) => setSignupPassword(e.target.value)}
                      onFocus={() => setPasswordFocused(true)}
                      onBlur={() => setPasswordFocused(false)}
                      placeholder="Min 8 characters with a number"
                      className="border-border text-foreground placeholder:text-muted-foreground focus:border-[hsl(var(--brand-teal))] focus:ring-2 focus:ring-[hsl(var(--brand-teal))]/20 rounded-lg py-5 pr-12"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-4 hover:bg-transparent text-muted-foreground hover:text-foreground"
                      onClick={() => setShowSignupPassword(!showSignupPassword)}
                    >
                      {showSignupPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </Button>
                  </div>
                  {(passwordFocused || signupPassword.length > 0) && (
                    <div className="mt-2 space-y-1">
                      {[
                        { met: hasMinLength, label: 'At least 8 characters' },
                        { met: hasLetter, label: 'At least 1 letter' },
                        { met: hasNumber, label: 'At least 1 number' },
                      ].map((req) => (
                        <div key={req.label} className="flex items-center gap-1.5">
                          <CheckCircle className={`w-3.5 h-3.5 ${req.met ? 'text-primary' : 'text-muted-foreground/40'}`} />
                          <span className={`text-xs ${req.met ? 'text-primary' : 'text-muted-foreground'}`}>{req.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup_confirm_password" className="text-foreground text-sm font-medium">Confirm Password</Label>
                  <div className="relative">
                    <Input
                      id="signup_confirm_password"
                      type={showSignupConfirmPassword ? "text" : "password"}
                      value={signupConfirmPassword}
                      onChange={(e) => setSignupConfirmPassword(e.target.value)}
                      placeholder="Re-enter your password"
                      className="border-border text-foreground placeholder:text-muted-foreground focus:border-[hsl(var(--brand-teal))] focus:ring-2 focus:ring-[hsl(var(--brand-teal))]/20 rounded-lg py-5 pr-12"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-4 hover:bg-transparent text-muted-foreground hover:text-foreground"
                      onClick={() => setShowSignupConfirmPassword(!showSignupConfirmPassword)}
                    >
                      {showSignupConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </Button>
                  </div>
                  {/* Real-time password match indicator (visual only) */}
                  <div className="min-h-[1.125rem]" aria-live="polite">
                    {signupConfirmPassword.length > 0 && signupPassword.length > 0 && (
                      signupPassword === signupConfirmPassword ? (
                        <div className="flex items-center gap-1.5">
                          <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
                          <span className="text-xs text-primary">Passwords match</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <XCircle className="w-3.5 h-3.5 text-destructive" />
                          <span className="text-xs text-destructive">Passwords don't match</span>
                        </div>
                      )
                    )}
                  </div>
                </div>

                <div className="flex items-start space-x-3 pt-2">
                  <Checkbox
                    id="accept_terms"
                    checked={signupAcceptedTerms}
                    onCheckedChange={(checked) => setSignupAcceptedTerms(checked as boolean)}
                    className="border-border data-[state=checked]:bg-[hsl(var(--brand-teal))] data-[state=checked]:border-[hsl(var(--brand-teal))] mt-1"
                  />
                  <Label
                    htmlFor="accept_terms"
                    className="text-sm text-muted-foreground cursor-pointer font-normal leading-relaxed"
                  >
                    I agree to the{' '}
                    <a href="/terms" className="text-[hsl(var(--brand-teal))] hover:underline">Terms and Conditions</a>
                    {' '}and{' '}
                    <a href="/privacy" className="text-[hsl(var(--brand-teal))] hover:underline">Privacy Policy</a>
                  </Label>
                </div>

                <Button
                  onClick={handleSignup}
                  disabled={isLoading || !signupAcceptedTerms || !isPasswordValid || !isPhoneValid}
                  className="w-full bg-[hsl(var(--brand-teal))] hover:bg-[hsl(var(--brand-teal))]/90 text-white font-semibold py-5 rounded-lg transition-all mt-4 disabled:opacity-50"
                >
                  {isLoading ? 'Creating account…' : 'Create Account'}
                </Button>
                {activeTab === 'signup' && (
                  <>
                    <button
                      type="button"
                      onClick={handleStartOver}
                      className="block w-full text-center text-sm text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors mt-3"
                    >
                      Start over
                    </button>
                    <p className="text-xs text-muted-foreground text-center mt-2">
                      Having issues? Please email{' '}
                      <a href="mailto:support@lanerunner.com" className="text-[hsl(var(--brand-teal))] hover:underline">
                        support@lanerunner.com
                      </a>
                    </p>
                  </>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <AlertDialog open={startOverOpen} onOpenChange={(open) => { if (!startOverLoading) setStartOverOpen(open); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Start over?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete your progress and return you to role selection.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={startOverLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmStartOver(); }}
              disabled={startOverLoading}
              className={buttonVariants({ variant: 'destructive' })}
            >
              {startOverLoading ? 'Deleting…' : 'Yes, start over'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
